/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Params, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { isValidCoordinates } from '../../../../utils/coordinates.utilities';
import { isElementNull } from '../../../../utils/validation.utilities';
import { ROUTE_PARAMS } from '../../../core/constants/route-params.constants';
import { LatLng } from '../../../core/models';
import { getManagePostedSpeedEnabled } from '../../../core/store/selectors';
import { IAppState } from '../../../core/store/state';
import { openManagePostedSpeedPanelFromDeeplink } from '../../../manage-posted-speed/store/actions/manage-posted-speed.actions';
import { IManageSpeedQueryParams } from '../../models';

const extractQueryParams = (queryParams: Params): IManageSpeedQueryParams => ({
  lat: queryParams[ROUTE_PARAMS.MANAGE_SPEED.LAT],
  lng: queryParams[ROUTE_PARAMS.MANAGE_SPEED.LNG],
  source: queryParams[ROUTE_PARAMS.MANAGE_SPEED.SOURCE]
});

const validateQueryParams = (queryParams: IManageSpeedQueryParams): boolean => {
  if (!queryParams.lat || !queryParams.lng) {
    return false;
  }

  const lat = Number.parseFloat(queryParams.lat);
  const lng = Number.parseFloat(queryParams.lng);

  return isValidCoordinates(lat, lng);
};

const parseCoordinates = (queryParams: IManageSpeedQueryParams): LatLng => {
  const lat = Number.parseFloat(queryParams.lat);
  const lng = Number.parseFloat(queryParams.lng);
  return new LatLng(lat, lng);
};

const handleError = (router: Router, _error: unknown): Observable<boolean> => {
  router.navigate(['/']);
  return of(false);
};

const checkPermissions = (store: Store<IAppState>): Observable<boolean> => {
  return store.pipe(
    select(getManagePostedSpeedEnabled),
    filter((permission): permission is boolean => !isElementNull(permission)),
    take(1)
  );
};

export const manageSpeedByQueryParamResolver: ResolveFn<boolean> = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): Observable<boolean> => {
  const store = inject(Store<IAppState>);
  const router = inject(Router);

  const queryParams = extractQueryParams(route.queryParams);

  if (!validateQueryParams(queryParams)) {
    return handleError(router, new Error('Missing required lat/lng parameters'));
  }

  const coordinates = parseCoordinates(queryParams);

  return checkPermissions(store).pipe(
    switchMap(hasPermission => {
      if (!hasPermission) {
        return handleError(router, new Error('User does not have permission to manage posted speed'));
      }
      // Dispatch action and return immediately - don't wait for map to load
      store.dispatch(openManagePostedSpeedPanelFromDeeplink({ coordinates }));
      return of(true);
    }),
    catchError(error => handleError(router, error))
  );
};
